/**
 * Canonical stringify + change-detection hash.
 *
 * The hash is only ever compared against another hash produced here — it is
 * never a security boundary — so it uses a synchronous 128-bit FNV-1a variant
 * rather than SHA-256. `crypto.subtle.digest` is async, and making it async
 * would force the whole planner (deliberately pure and synchronous) to become
 * promise-based for no benefit.
 */

/**
 * Deterministic JSON: object keys sorted, `undefined` dropped, array order kept.
 * Two structurally equal payloads always stringify identically.
 */
export function canonicalStringify(value: unknown): string {
  return JSON.stringify(canonicalize(value))
}

function canonicalize(value: unknown): unknown {
  if (value === null || typeof value !== 'object') {
    return value === undefined ? null : value
  }
  if (Array.isArray(value)) {
    return value.map(canonicalize)
  }

  const source = value as Record<string, unknown>
  const out: Record<string, unknown> = {}
  for (const key of Object.keys(source).sort()) {
    if (source[key] === undefined) continue
    out[key] = canonicalize(source[key])
  }
  return out
}

const FNV_OFFSET = 0x811c9dc5
const FNV_PRIME = 0x01000193

/** 32 hex chars from four independently seeded FNV-1a passes. */
export function hashObject(value: unknown): string {
  return hashString(canonicalStringify(value))
}

export function hashString(input: string): string {
  const seeds = [FNV_OFFSET, 0x1000193, 0x9e3779b9, 0x85ebca6b]
  let out = ''

  for (const seed of seeds) {
    let h = seed >>> 0
    for (let i = 0; i < input.length; i++) {
      h ^= input.charCodeAt(i)
      h = Math.imul(h, FNV_PRIME) >>> 0
    }
    out += h.toString(16).padStart(8, '0')
  }

  return out
}
